import type { CallbackInterface } from "recoil";

import { logAppError } from "Helpers/logger";
import { errorUpdater, loaderUpdater, pageState } from "State/page";

import { panelEditRecipientOpenCallback } from "Pages/RecipientsList/recoil/callbacks/recipient";
import { fetchMobilePrefixes } from "Pages/RecipientsList/recoil/services/common";
import { updateContacts } from "Pages/RecipientsList/recoil/services/editContacts";
import {
	notifyEditContactsError,
	notifyEditContactsSuccess,
} from "Pages/RecipientsList/recoil/services/notifications";
import { fetchRecipient } from "Pages/RecipientsList/recoil/services/recipient";
import { dynamicFieldsState } from "Pages/RecipientsList/recoil/state/columns";
import {
	countryCodesState,
	maskedFieldsState,
} from "Pages/RecipientsList/recoil/state/common";
import { panelEditContactsState } from "Pages/RecipientsList/recoil/state/editContacts";
import { panelEditRecipientState } from "Pages/RecipientsList/recoil/state/recipient";
import { gainsightTrack } from "mailup-react-helpers";

export const panelEditContactsOpenCallback =
	({ set, snapshot, reset }: CallbackInterface) =>
	async (idRecipient: number) => {
		set(pageState, loaderUpdater(true));

		const [{ loaded }, maskedFields, dynamicFields] = await Promise.all([
			snapshot.getPromise(countryCodesState),
			snapshot.getPromise(maskedFieldsState),
			snapshot.getPromise(dynamicFieldsState),
		]);

		try {
			const { Email, MobilePrefix, MobileNumber } = await fetchRecipient(
				idRecipient,
				dynamicFields,
			);

			if (!loaded) {
				const countryCodes = await fetchMobilePrefixes();
				set(countryCodesState, (prev) => ({
					...prev,
					items: countryCodes,
					loaded: true,
				}));
			}

			const EmailDisabled = maskedFields.includes("Email");
			const SMSDisabled = maskedFields.includes("MobileNumber");

			set(panelEditContactsState, {
				show: true,
				Email,
				EmailDisabled,
				EmailRequired: Email.length > 0 && !EmailDisabled,
				MobilePrefix,
				MobileNumber,
				SMSDisabled: maskedFields.includes("MobileNumber"),
				SMSRequired: MobileNumber.length > 0 && !SMSDisabled,
				idRecipient,
				isDirty: false,
			});

			reset(panelEditRecipientState);
		} catch (error) {
			console.error("### error", error);

			logAppError({
				Component: "RecipientsList",
				Method: "panelEditRecipientOpenCallback",
				appError: error,
			});

			set(pageState, errorUpdater);
		}

		set(pageState, loaderUpdater(false));
	};

export const panelEditContactsCloseCallback =
	({ set, reset, snapshot }: CallbackInterface) =>
	async () => {
		gainsightTrack("mup_recipients_click_hideRightPanel", {});
		reset(panelEditContactsState);
	};

export const panelEditContactsBackCallback =
	({ set, reset, snapshot, ...cbi }: CallbackInterface) =>
	async () => {
		const { idRecipient } = await snapshot.getPromise(panelEditContactsState);
		reset(panelEditContactsState);

		void panelEditRecipientOpenCallback({ ...cbi, set, reset, snapshot })(
			idRecipient,
		);
	};

export const panelEditContactsChangeFieldCallback =
	({ set }: CallbackInterface) =>
	async (
		f: "Email" | "MobilePrefix" | "MobileNumber",
		value: string | undefined,
	) => {
		set(panelEditContactsState, (prev) => ({
			...prev,
			[f]: value === undefined ? "" : value,
			isDirty: true,
		}));
	};

export const panelEditContactsSaveCallback =
	({ reset, set, snapshot, ...cbi }: CallbackInterface) =>
	async () => {
		set(pageState, loaderUpdater(true));

		const {
			idRecipient,
			Email,
			MobilePrefix,
			MobileNumber,
			EmailDisabled,
			SMSDisabled,
		} = await snapshot.getPromise(panelEditContactsState);

		try {
			const res = await updateContacts(idRecipient, {
				Email,
				MobilePrefix,
				MobileNumber,
				EmailDisabled,
				SMSDisabled,
			});

			if (res === "OK") {
				//  TODO bnotify success - reset edit contacts - open edit recipient
				notifyEditContactsSuccess();

				reset(panelEditContactsState);

				void panelEditRecipientOpenCallback({ ...cbi, set, reset, snapshot })(
					idRecipient,
				);
			} else {
				notifyEditContactsError(res, {
					email: Email,
					sms: `${MobilePrefix} ${MobileNumber}`,
				});
			}
		} catch (error) {
			console.error("### error", error);

			logAppError({
				Component: "RecipientsList",
				Method: "panelEditContactsSaveCallback",
				appError: error,
			});

			set(pageState, errorUpdater);
		}

		set(pageState, loaderUpdater(false));
	};
